import {Graphics, Rectangle} from "pixi.js";
import {inches} from "../../const";
import {glyphStyle, GlyphFootprintContext, GlyphRect, hasCategory, HardwareGlyph, hitAreaOf, strikeEdgeX} from "./HardwareGlyph";

// an exit device lever trim seen from the pull side: the lever on its rose at the hub
// (the spindle, in line with the device's chassis on the other face), reaching from the
// strike side toward the hinge edge. An escutcheon trim adds the tall rounded plate
// behind it, and a keyed function (storeroom / classroom) the cylinder above the lever.
// Sizes are typical catalogue values (scene units = mm).
const PLATE_WIDTH = 2.75 * inches;
const PLATE_HEIGHT = 10.5 * inches;
const PLATE_RADIUS = 1 * inches;
const PLATE_ABOVE_HUB = 6.5 * inches;      // plate top above the spindle
const CYLINDER_ABOVE_HUB = 3.75 * inches;
const CYLINDER_RADIUS = 0.625 * inches;
const ROSE_RADIUS = 1.125 * inches;
const LEVER_LENGTH = 4.5 * inches;
const LEVER_THICKNESS = 0.75 * inches;
const OUTLINE = 0.25 * inches;
const HIT_PAD = 1 * inches;

// edge-view projections off the door face (scene units = mm).
const PLATE_DEPTH = 0.25 * inches;
const ROSE_DEPTH = 0.5 * inches;
const LEVER_PROJECTION = 2.5 * inches;
const CYLINDER_PROJECTION = 0.375 * inches;

export const leverTrim: HardwareGlyph = {
    id: "leverTrim",
    targets: ["door"],
    matches: item => hasCategory(item, "Lever Trims"),
    draw(g: Graphics, ctx): Rectangle {
        const {color, alpha} = glyphStyle(ctx.selected, ctx.invalid);
        const {cx, cy, escutcheon, keyed, plateTop, leverEnd, box} = layout(ctx);

        if(escutcheon) {
            g.lineStyle(OUTLINE, color, alpha);
            g.beginFill(color, alpha * 0.2);
            g.drawRoundedRect(cx - PLATE_WIDTH / 2, plateTop, PLATE_WIDTH, PLATE_HEIGHT, PLATE_RADIUS);
            g.endFill();
        }

        if(keyed) {
            g.lineStyle(OUTLINE, color, alpha);
            g.beginFill(color, alpha * 0.35);
            g.drawCircle(cx, cy - CYLINDER_ABOVE_HUB, CYLINDER_RADIUS);
            g.endFill();
        }

        // lever: solid, from the spindle toward the hinge edge, rounded at the grip end.
        g.lineStyle(0);
        g.beginFill(color, alpha);
        g.drawRoundedRect(Math.min(cx, leverEnd), cy - LEVER_THICKNESS / 2, LEVER_LENGTH, LEVER_THICKNESS, LEVER_THICKNESS / 2);
        g.endFill();

        // rose at the hub so the placement point stays visible.
        g.lineStyle(OUTLINE, color, alpha);
        g.beginFill(color, alpha * 0.35);
        g.drawCircle(cx, cy, ROSE_RADIUS);
        g.endFill();

        return hitAreaOf([box], HIT_PAD);
    },
    footprint(ctx): GlyphRect[] {
        return [layout(ctx).box];
    },
    // seen from the latch edge: the plate flat on the face, the rose standing off it, and the
    // lever reaching straight out (it lies parallel to the face, so only its depth shows).
    drawEdge(g: Graphics, ctx): Rectangle {
        const {color, alpha} = glyphStyle(ctx.selected, ctx.invalid);
        const fx = ctx.face.x;
        const cy = ctx.face.y;
        const out = ctx.out;

        const escutcheon = hasCategory(ctx.item, "Classroom Escutcheon Trim", "Dummy Escutcheon Trim", "Passage Escutcheon Trim", "Storeroom Escutcheon Trim");
        const keyed = hasCategory(ctx.item, "Classroom Escutcheon Trim", "Storeroom Escutcheon Trim", "Classroom Lever Trim", "Storeroom Lever Trim");

        let base = fx;
        if(escutcheon) {
            g.lineStyle(OUTLINE, color, alpha);
            g.beginFill(color, alpha * 0.2);
            rect(g, fx, fx + out * PLATE_DEPTH, cy - PLATE_ABOVE_HUB, PLATE_HEIGHT);
            g.endFill();
            base = fx + out * PLATE_DEPTH;
        }

        if(keyed) {
            g.lineStyle(OUTLINE, color, alpha);
            g.beginFill(color, alpha * 0.35);
            rect(g, base, base + out * CYLINDER_PROJECTION, cy - CYLINDER_ABOVE_HUB - CYLINDER_RADIUS, CYLINDER_RADIUS * 2);
            g.endFill();
        }

        g.lineStyle(OUTLINE, color, alpha);
        g.beginFill(color, alpha * 0.35);
        rect(g, base, base + out * ROSE_DEPTH, cy - ROSE_RADIUS, ROSE_RADIUS * 2);
        g.endFill();

        const leverEnd = base + out * (ROSE_DEPTH + LEVER_PROJECTION);
        g.lineStyle(0);
        g.beginFill(color, alpha);
        rect(g, base + out * ROSE_DEPTH, leverEnd, cy - LEVER_THICKNESS / 2, LEVER_THICKNESS);
        g.endFill();

        const y0 = escutcheon ? cy - PLATE_ABOVE_HUB : keyed ? cy - CYLINDER_ABOVE_HUB - CYLINDER_RADIUS : cy - ROSE_RADIUS;
        const y1 = escutcheon ? cy - PLATE_ABOVE_HUB + PLATE_HEIGHT : cy + ROSE_RADIUS;
        const x0 = Math.min(fx, leverEnd);
        const x1 = Math.max(fx, leverEnd);
        return new Rectangle(x0 - HIT_PAD, y0 - HIT_PAD, x1 - x0 + 2 * HIT_PAD, y1 - y0 + 2 * HIT_PAD);
    },
};

// the face-on geometry: the rose at the hub with the lever off it toward the hinge edge,
// the plate behind (escutcheon trims) and the cylinder above (keyed functions). `box` is
// the extent of whichever of those the item has. `dir` points toward the hinge edge.
function layout(ctx: GlyphFootprintContext) {
    const cx = ctx.hub.x;
    const cy = ctx.hub.y;
    const dir = strikeEdgeX(ctx).dir;

    const escutcheon = hasCategory(ctx.item, "Classroom Escutcheon Trim", "Dummy Escutcheon Trim", "Passage Escutcheon Trim", "Storeroom Escutcheon Trim");
    const keyed = hasCategory(ctx.item, "Classroom Escutcheon Trim", "Storeroom Escutcheon Trim", "Classroom Lever Trim", "Storeroom Lever Trim");

    const plateTop = cy - PLATE_ABOVE_HUB;
    const leverEnd = cx + dir * LEVER_LENGTH;

    const box: GlyphRect = {
        x0: Math.min(cx - ROSE_RADIUS, leverEnd, escutcheon ? cx - PLATE_WIDTH / 2 : Infinity),
        x1: Math.max(cx + ROSE_RADIUS, leverEnd, escutcheon ? cx + PLATE_WIDTH / 2 : -Infinity),
        y0: escutcheon ? plateTop : keyed ? cy - CYLINDER_ABOVE_HUB - CYLINDER_RADIUS : cy - ROSE_RADIUS,
        y1: escutcheon ? plateTop + PLATE_HEIGHT : cy + ROSE_RADIUS,
    };

    return {cx, cy, dir, escutcheon, keyed, plateTop, leverEnd, box};
}

// a rect between two x positions given in either order.
function rect(g: Graphics, xa: number, xb: number, y: number, h: number) {
    g.drawRect(Math.min(xa, xb), y, Math.abs(xb - xa), h);
}
